import { Controller } from "@hotwired/stimulus"

// One toast. Two jobs: make sure it is somewhere it can actually be seen and clicked, and
// dismiss itself.
//
// Registered in *both* bundles (controllers/index.js and portal.js) from one file, like
// `scroll-to-end`. The two surfaces share no partials or styling — the toast markup is
// themed per surface by view path — but the behaviour here is the same on both, and two
// copies of it would be two things to fix.
//
// **Containment, not top-layer ordering.** A modal `<dialog>` lives in the browser's top
// layer, which paints above every normal-flow element no matter what z-index it carries, so
// a toast in the body is behind an open drawer. The first version answered that by making
// the page's container a `popover` — also a top-layer element — on the theory that top-layer
// elements stack in promotion order, so re-showing it would put it above any open dialog.
// In practice the promotion did not take, and the failure was worse than invisible: the
// container sat behind the drawer's *semi-transparent* backdrop, so the toast could be seen
// but every click on it landed on the backdrop, and turbo_overlay reads a backdrop click as
// a dismiss. Clicking the toast closed the drawer.
//
// So this relies on nothing but the DOM tree: a toast raised from inside an overlay stays
// inside it, and one raised anywhere else moves into the topmost open overlay if there is
// one. A descendant always paints above its ancestor and always receives its own clicks —
// no ordering, no feature detection, no browser differences.
//
// `.toast-host` is the fixed-position box the toasts sit in. Every layout renders one
// (`#toasts`), and so does every overlay chrome partial, which is what gives a toast raised
// inside a drawer somewhere to go.
export default class extends Controller {
  static values = { timeout: { type: Number, default: 6000 } }

  connect() {
    this.#moveIntoTopmostHost()
    // 0 means "stays until dismissed" — for anything a user may need to read twice, or
    // copy out of.
    if (this.timeoutValue > 0) {
      this.timer = setTimeout(() => this.dismiss(), this.timeoutValue)
    }
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  dismiss() {
    this.element.remove()
  }

  // Pause the countdown while the pointer is over the toast, or focus is inside it: a
  // message worth reading is one worth not having snatched away mid-sentence.
  hold() {
    clearTimeout(this.timer)
  }

  resume() {
    if (this.timeoutValue > 0) {
      this.timer = setTimeout(() => this.dismiss(), this.timeoutValue)
    }
  }

  // Already inside an open overlay — where the chrome rendered it — so it is already above
  // everything and already clickable. Nothing to do.
  //
  // Otherwise: if an overlay is open, this toast is in the layout's container underneath it
  // and has to move in. That is the Turbo Stream case — a later request or a background job
  // appending to `#toasts` while the user has a drawer open.
  #moveIntoTopmostHost() {
    if (this.element.closest("dialog[open]")) return

    // Document order is promotion order closely enough for "which one is the user looking
    // at": the gem appends each new overlay to the end of its stack. Templates are inert —
    // `<template>` content is not in the document tree — so they cannot match here.
    const overlays = document.querySelectorAll("dialog[open]")
    const host = overlays[overlays.length - 1]?.querySelector(".toast-host")

    if (host && this.element.parentElement !== host) host.appendChild(this.element)
  }
}
